'use client';

import { useCallback } from 'react';
import { useCompleteTourMutation } from '@/api/features/users/endpoints/profile';
import { apiLogger } from '@/core/utils/logger';
import { extractErrorMessage } from '@/shared/utils/error';
import { useTour } from './useTour';

/**
 * Returns a callback that records tour completion on the backend,
 * then resets local tour state via completeTour().
 *
 * Fire-and-forget — the local tour always completes even if the
 * API call fails (graceful degradation).
 */
export function useCompleteTourWithTracking() {
  const { completeTour } = useTour();
  const [completeTourMutation] = useCompleteTourMutation();

  const completeTourWithTracking = useCallback(
    (tourId: string) => {
      // Fire-and-forget: don't await, don't block tour completion
      completeTourMutation(tourId).catch((err: unknown) => {
        apiLogger.warn('[completeTourWithTracking] API call failed (non-blocking)', {
          tourId,
          error: extractErrorMessage(err),
        });
      });
      completeTour();
    },
    [completeTourMutation, completeTour],
  );

  return { completeTourWithTracking };
}
